"use client"

import type React from "react"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { GenericTooltip } from "@/components/generic-tooltip"
import { PropertyBasicEditModal } from "@/components/admin/property-basic-edit-modal"
import { useAuth } from "@/contexts/AuthContext"
import { MapPin, Users, Bath, UtensilsCrossed, Edit } from "lucide-react"

interface Property {
  id: number
  name: string
  price: number
  location: string
  neighborhood?: string
  bathrooms: number
  roommates: number
  diningRooms?: number
  area?: number
  image: string
  coordinates: [number, number]
  description: string
}

interface PropertyCardProps {
  property: Property
  compact?: boolean
  onPropertyUpdated?: () => void
}

export function PropertyCard({ property, compact = false, onPropertyUpdated }: PropertyCardProps) {
  const { isAuthenticated } = useAuth()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Truncate name if too long
  const truncateName = (name: string, maxLength: number) => {
    if (name.length <= maxLength) return name
    return name.substring(0, maxLength).trim() + "..."
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsEditModalOpen(true)
  }

  const handleEditSuccess = () => {
    onPropertyUpdated?.()
  }

  return (
    <>
      <Card className="bg-white text-raime-dark-blue rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 h-full flex flex-col">
        <CardHeader className="p-0 relative">
          <Image
            src={property.image || "/placeholder.svg"}
            alt={property.name}
            width={400}
            height={250}
            className={`w-full object-cover ${compact ? "h-40" : "h-52"}`}
          />
          {/* Edit Button - Only visible for authenticated users */}
          {isAuthenticated && (
            <div className="absolute top-2 right-2">
              <Button
                size="sm"
                onClick={handleEditClick}
                className="bg-raime-golden-brown hover:bg-raime-golden-brown/90 text-white shadow-lg"
              >
                <Edit className="h-3 w-3 mr-1" />
                Editar
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className={`${compact ? "p-4" : "p-6"} text-left flex-grow`}>
          <CardTitle className={`font-bold mb-2 ${compact ? "text-lg" : "text-xl"} leading-tight`}>
            {compact ? truncateName(property.name, 25) : truncateName(property.name, 35)}
          </CardTitle>
          <p className={`font-semibold text-raime-golden-brown mb-3 ${compact ? "text-lg" : "text-lg"}`}>
            €{property.price}/mes
          </p>
          <div className={`flex items-center text-sm text-muted-foreground mb-3 ${compact ? "mb-2" : "mb-3"}`}>
            <MapPin className="h-4 w-4 mr-2 text-gray-600 flex-shrink-0" />
            <span className="truncate">{property.location}</span>
          </div>
          <div className={`grid grid-cols-3 gap-2 text-sm text-muted-foreground ${compact ? "text-xs" : "text-sm"}`}>
            <GenericTooltip content="Compañeros">
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1 text-gray-600 flex-shrink-0" />
                <span className="truncate">{property.roommates}</span>
              </div>
            </GenericTooltip>

            <GenericTooltip content="Baños">
              <div className="flex items-center">
                <Bath className="h-4 w-4 mr-1 text-gray-600 flex-shrink-0" />
                <span className="truncate">{property.bathrooms}</span>
              </div>
            </GenericTooltip>

            <GenericTooltip content="Comedor">
              <div className="flex items-center">
                <UtensilsCrossed className="h-4 w-4 mr-1 text-gray-600 flex-shrink-0" />
                <span className="truncate">{property.diningRooms || 1}</span>
              </div>
            </GenericTooltip>
          </div>
        </CardContent>
        <CardFooter className={`${compact ? "p-4 pt-0" : "p-6 pt-0"} mt-auto`}>
          <Link href={`/propiedades/${property.id}`} className="w-full">
            <Button className="w-full bg-raime-dark-blue hover:bg-raime-dark-blue/90 text-white rounded-lg">
              Ver Detalles
            </Button>
          </Link>
        </CardFooter>
      </Card>

      {/* Edit Modal */}
      <PropertyBasicEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        propertyId={property.id}
        onSuccess={handleEditSuccess}
      />
    </>
  )
}
